<template>
    <li
        :id="optionId"
        role="option"
        :aria-selected="isActive"
        :aria-label="optionLabel"
        :aria-disabled="isDisabled || undefined"
        :aria-describedby="isDisabled ? disabledExplanationId : undefined"
        class="px-2.5 py-1 text-sm"
        :class="appearanceClasses"
        @mousedown.prevent
        @mousemove="dialogContext.setActiveOption(optionId)"
        @mouseleave="dialogContext.clearActiveOption(optionId)"
        @click="onClick"
    >
        <div class="flex items-center gap-2">
            <UserReferencePersonInfo :user="props.user" :name-highlight="dialogContext.query.value" />

            <Badge v-if="!props.user.hasLoginMethod" variant="Gray" size="Extra-Small">
                <template #icon>
                    <BlockIcon class="size-4" />
                </template>
                {{ $t('no-login-method') }}
            </Badge>

            <Tooltip :is-teleported="true" :is-open="isActive">
                <template #trigger>
                    <span class="flex w-max items-center rounded-sm" :class="iconButtonClasses">
                        <PersonRemoveIcon v-if="props.isAssigned" class="size-4.5" />
                        <PersonAddIcon v-else class="size-4.5" />
                    </span>
                </template>
                <template #text>
                    <div class="w-max">{{ disabledExplanation ?? actionText }}</div>
                </template>
            </Tooltip>
        </div>
        <span v-if="isDisabled" :id="disabledExplanationId" class="sr-only">{{ disabledExplanation }}</span>
    </li>
</template>

<script setup lang="ts">
import { useFluent } from 'fluent-vue';
import { computed, inject, onBeforeUnmount, useId } from 'vue';
import Badge from '../Badge/Badge.vue';
import { getSizeClasses, getVariantClasses } from '../Button/Button.styles';
import BlockIcon from '../Icon/BlockIcon.vue';
import PersonAddIcon from '../Icon/PersonAddIcon.vue';
import PersonRemoveIcon from '../Icon/PersonRemoveIcon.vue';
import Tooltip from '../Tooltip/Tooltip.vue';
import type { UserReferenceUser } from './UserReference.types';
import UserReferencePersonInfo from './UserReferencePersonInfo.vue';
import { userReferenceDialogKey } from './injectionKeys';

type UserReferenceUserOptionProps = {
    user: UserReferenceUser;
    isAssigned?: boolean;
};

type UserReferenceUserOptionEmits = {
    (e: 'select'): void;
};

const props = defineProps<UserReferenceUserOptionProps>();
const emit = defineEmits<UserReferenceUserOptionEmits>();

const { $t } = useFluent();

const dialogContext = inject(userReferenceDialogKey);
if (!dialogContext) {
    throw new Error('UserReferenceUserOption must be used within a UserReferenceDialog component');
}

const optionId = `user-reference-option-${useId()}`;
const disabledExplanationId = `${optionId}-disabled`;

const isActive = computed(() => dialogContext.activeOptionId.value === optionId);

// Only assigning can be limited, unassigning is always possible
const disabledExplanation = computed(() =>
    props.isAssigned ? undefined : dialogContext.assignDisabledExplanation.value,
);
const isDisabled = computed(() => Boolean(disabledExplanation.value));

function onClick() {
    if (!isDisabled.value) {
        emit('select');
    }
}

// Selecting an option usually removes it from the results, which must not leave a dangling aria-activedescendant
onBeforeUnmount(() => dialogContext.clearActiveOption(optionId));

const actionText = computed(() => {
    const name = `${props.user.firstName} ${props.user.lastName}`;
    return props.isAssigned ? $t('unassign-user-action', { name }) : $t('assign-user-action', { name });
});

// Read as one phrase instead of every visual fragment in DOM order, which would start with the company
const optionLabel = computed(() =>
    [
        actionText.value,
        props.user.company?.name,
        props.user.jobTitle,
        props.user.hasLoginMethod ? undefined : $t('no-login-method'),
    ]
        .filter(Boolean)
        .join(', '),
);

// Highlighted while active, by hovering or the arrow keys
const appearanceClasses = computed(() => {
    if (isDisabled.value) {
        return 'cursor-not-allowed aria-selected:bg-gray-200';
    }

    return props.isAssigned ? 'cursor-pointer aria-selected:bg-red-100' : 'cursor-pointer aria-selected:bg-sky-100';
});

// The variant, size and focus ring of Button.styles, with the ring shown while this option is active
const iconButtonClasses = computed(() => {
    let ringColorClass = props.isAssigned ? 'outline-red-700' : 'outline-sky-700';
    if (isDisabled.value) {
        ringColorClass = 'outline-gray-400';
    }

    return [
        getVariantClasses(isDisabled.value, props.isAssigned ? 'Danger-Light' : 'Info-Light'),
        getSizeClasses('Small', true, false, false),
        isActive.value && ['outline-2 outline-offset-2', ringColorClass],
    ];
});
</script>
